import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { profileAPI } from '../services/api';
import Toast from '../components/Toast';
import FileUpload from '../components/FileUpload';
import AlbumFormModal from '../components/AlbumFormModal';
import { ARTIST_TYPES } from '../utils/artistTypes';
import {
    ArrowLeft, Save, User, MapPin, Type,
    Loader, AlertCircle, Disc3, Edit3, Image as ImageIcon,
    Star, ToggleLeft, ToggleRight, Plus, X, Trash2, Check
} from 'lucide-react';

const EditProfile = () => {
    const navigate = useNavigate();
    const { user, updateUser } = useAuth();
    
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [deleting, setDeleting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'error' });
    
    // Form data state
    const [formData, setFormData] = useState({
        username: '',
        displayName: '',
        artistName: '',
        bio: '',
        location: '',
        profilePictureUrl: '',
        isArtist: false,
        // Artist fields — a profile can be multiple types of artist at once
        // (e.g. music + painting), each with its own set of selected genres/styles.
        artistTypes: [],
        customArtistType: '', // free-text label shown in place of "Other"
        genres: {} // { [artistTypeKey]: string[] }
    });

    // Which type's genre dropdown is currently open (only one at a time)
    const [openGenreDropdown, setOpenGenreDropdown] = useState(null);

    // Album management
    const [albums, setAlbums] = useState([]);
    const [albumsLoading, setAlbumsLoading] = useState(true);
    const [showAlbumModal, setShowAlbumModal] = useState(false);
    const [editingAlbum, setEditingAlbum] = useState(null);
    const [deletingAlbumId, setDeletingAlbumId] = useState(null);

    const loadAlbums = async () => {
        try {
            setAlbumsLoading(true);
            const response = await api.get('/albums/mine');
            if (response.data.success) {
                setAlbums(response.data.data);
            }
        } catch (error) {
            console.error('Error loading albums:', error);
        } finally {
            setAlbumsLoading(false);
        }
    };

    useEffect(() => {
        if (user?.profile?.username) loadAlbums();
    }, [user]);

    const handleCreateAlbum = () => {
        setEditingAlbum(null);
        setShowAlbumModal(true);
    };

    const handleEditAlbum = (album) => {
        setEditingAlbum(album);
        setShowAlbumModal(true);
    };

    const handleDeleteAlbum = async (album) => {
        if (!window.confirm(`Delete the album "${album.title}"? This cannot be undone.`)) {
            return;
        }
        setDeletingAlbumId(album.id);
        try {
            await api.delete(`/albums/${album.id}`);
            setAlbums(prev => prev.filter(a => a.id !== album.id));
            setToast({ show: true, message: 'Album deleted', type: 'success' });
        } catch (error) {
            console.error('Error deleting album:', error);
            setToast({ show: true, message: 'Failed to delete album', type: 'error' });
        } finally {
            setDeletingAlbumId(null);
        }
    };

    // Load current profile data
    useEffect(() => {
        const loadProfileData = async () => {
            try {
                if (!user?.profile?.username) {
                    navigate('/dashboard');
                    return;
                }

                const response = await profileAPI.getProfile(user.profile.username);
                const profile = response.profile;

                setFormData({
                    username: profile.username || '',
                    displayName: profile.displayName || '',
                    artistName: profile.artistName || '',
                    bio: profile.bio || '',
                    location: profile.location || '',
                    profilePictureUrl: profile.profilePictureUrl || '',
                    isArtist: profile.isArtist || false,
                    artistTypes: profile.artistTypes || [],
                    customArtistType: profile.customArtistType || '',
                    genres: profile.genres || {}
                });
            } catch (error) {
                setToast({ show: true, message: 'Failed to load profile data', type: 'error' });
            } finally {
                setInitialLoading(false);
            }
        };

        loadProfileData();
    }, [user, navigate]);

    // Handle form input changes
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // Handle artist status toggle
    const handleArtistToggle = () => {
        setFormData(prev => ({
            ...prev,
            isArtist: !prev.isArtist
        }));
    };

    // Handle art type selection (multi-select — a profile can be several types of artist)
    const handleArtistTypeToggle = (typeKey) => {
        setFormData(prev => {
            const isSelected = prev.artistTypes.includes(typeKey);
            const artistTypes = isSelected
                ? prev.artistTypes.filter(t => t !== typeKey)
                : [...prev.artistTypes, typeKey];

            // Drop that type's genre selections once it's deselected
            const genres = { ...prev.genres };
            if (isSelected) delete genres[typeKey];

            // Clear the custom label once "Other" is deselected
            const customArtistType = (typeKey === 'other' && isSelected) ? '' : prev.customArtistType;

            return { ...prev, artistTypes, genres, customArtistType };
        });
        if (openGenreDropdown === typeKey) setOpenGenreDropdown(null);
    };

    // Handle genre/style selection, scoped to a specific art type
    const handleGenreToggle = (typeKey, genre) => {
        setFormData(prev => {
            const current = prev.genres[typeKey] || [];
            const updated = current.includes(genre)
                ? current.filter(g => g !== genre)
                : [...current, genre];
            return { ...prev, genres: { ...prev.genres, [typeKey]: updated } };
        });
    };

    // Handle profile image upload
    const handleImageUpload = (uploadData) => {
        setFormData(prev => ({
            ...prev,
            profilePictureUrl: uploadData.url
        }));
        setToast({ show: true, message: 'Profile image uploaded successfully!', type: 'success' });
    };

    // Handle upload errors
    const handleUploadError = (errorMessage) => {
        setToast({ show: true, message: errorMessage, type: 'error' });
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            // Validate required fields
            if (!formData.displayName.trim()) {
                setToast({ show: true, message: 'Display name is required', type: 'error' });
                return;
            }

            // Prepare data for submission
            const updateData = { ...formData };

            // Clean up artist fields if not an artist
            if (!formData.isArtist) {
                updateData.artistTypes = [];
                updateData.customArtistType = '';
                updateData.genres = {};
            }

            const response = await profileAPI.updateProfile(updateData);
            
            if (response.success) {
                setToast({ show: true, message: 'Profile updated successfully!', type: 'success' });
                
                // Update user context
                updateUser({ 
                    ...user, 
                    profile: { ...user.profile, ...response.profile } 
                });
                
                // Navigate back to profile after a delay
                setTimeout(() => {
                    navigate(`/profile/${formData.username}`);
                }, 1500);
            }
        } catch (error) {
            setToast({ 
                show: true, 
                message: error.message || 'Failed to update profile', 
                type: 'error' 
            });
        } finally {
            setLoading(false);
        }
    };

    // Delete profile
    const handleDeleteProfile = async () => {
        if (!window.confirm('Are you sure you want to delete your profile? This action cannot be undone.')) {
            return;
        }
        
        setDeleting(true);
        try {
            await profileAPI.deleteProfile();
            setToast({ show: true, message: 'Profile deleted successfully', type: 'success' });
            // Logout and redirect after a delay
            setTimeout(() => {
                localStorage.removeItem('deepwave_token');
                navigate('/');
                window.location.reload();
            }, 2000);
        } catch (error) {
            console.error('Delete profile error:', error);
            setToast({ 
                show: true, 
                message: error.message || 'Failed to delete profile', 
                type: 'error' 
            });
        } finally {
            setDeleting(false);
        }
    };

    // Loading state
    if (initialLoading) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <Loader className="w-8 h-8 animate-spin text-purple-500 mx-auto mb-4" />
                    <p className="text-gray-400">Loading profile...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 py-8">
            {/* Background Elements */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl animate-pulse-slow"></div>
                <div className="absolute bottom-20 right-10 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl animate-pulse-slow delay-200"></div>
            </div>

            <div className="relative z-10 max-w-2xl mx-auto px-6">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={() => navigate(`/profile/${user?.profile?.username}`)}
                        className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors duration-300 mb-6"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        Back to Profile
                    </button>
                    
                    <h1 className="text-4xl font-black gradient-text mb-2">
                        Edit Profile
                    </h1>
                    <p className="text-gray-400">
                        Update your profile information and preferences
                    </p>
                </div>

                {/* My Albums — above all other sections */}
                <div className="glass-card p-8 mb-8">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                            <Disc3 className="w-6 h-6 text-purple-400" />
                            My Albums
                        </h2>
                        <button
                            type="button"
                            onClick={handleCreateAlbum}
                            className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors"
                        >
                            <Plus className="w-3.5 h-3.5" />
                            Create Album
                        </button>
                    </div>

                    {albumsLoading ? (
                        <div className="text-center py-8">
                            <Loader className="w-6 h-6 animate-spin text-purple-500 mx-auto" />
                        </div>
                    ) : albums.length === 0 ? (
                        <p className="text-gray-400 text-center py-8 border-t border-white/10">
                            You haven't created any albums yet. Group your own tracks, videos, or images into a collection.
                        </p>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {albums.map((album) => (
                                <div key={album.id} className="flex items-center gap-3 bg-gray-800/50 rounded-xl p-3">
                                    <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-900 flex-shrink-0 flex items-center justify-center">
                                        {album.cover_image_url ? (
                                            <img
                                                src={album.cover_image_url}
                                                alt={album.title}
                                                width="56"
                                                height="56"
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <ImageIcon className="w-6 h-6 text-gray-600" />
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-white font-medium truncate">{album.title}</p>
                                        <p className="text-xs text-gray-400">{album.item_count || 0} item{album.item_count === 1 ? '' : 's'}</p>
                                    </div>
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                        <button
                                            type="button"
                                            onClick={() => handleEditAlbum(album)}
                                            aria-label="Edit album"
                                            className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                                        >
                                            <Edit3 className="w-4 h-4" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleDeleteAlbum(album)}
                                            disabled={deletingAlbumId === album.id}
                                            aria-label="Delete album"
                                            className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                                        >
                                            {deletingAlbumId === album.id ? (
                                                <Loader className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <Trash2 className="w-4 h-4" />
                                            )}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Edit Form */}
                <form onSubmit={handleSubmit} className="space-y-8">
                    {/* Basic Information */}
                    <div className="glass-card p-8">
                        <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                            <User className="w-6 h-6 text-purple-400" />
                            Basic Information
                        </h2>

                        <div className="space-y-6">
                            {/* Profile Picture */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-300 mb-2">
                                    Profile Picture
                                </label>
                                <div className="flex items-start gap-6">
                                    {/* Current Image */}
                                    <div className="flex-shrink-0">
                                        {formData.profilePictureUrl ? (
                                            <img
                                                src={formData.profilePictureUrl}
                                                alt="Current profile"
                                                width="80"
                                                height="80"
                                                className="w-20 h-20 rounded-full object-cover border-2 border-gray-600"
                                            />
                                        ) : (
                                            <div className="w-20 h-20 rounded-full bg-gray-700 border-2 border-gray-600 flex items-center justify-center">
                                                <User className="w-8 h-8 text-gray-400" />
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Upload Component */}
                                    <div className="flex-1">
                                        <FileUpload
                                            type="image"
                                            isProfileImage={true}
                                            onUpload={handleImageUpload}
                                            onError={handleUploadError}
                                            maxSizeMB={5}
                                            className="max-w-md"
                                            showPreview={false}
                                        >
                                            <span className="text-purple-400">Upload new image</span> or drag and drop
                                        </FileUpload>
                                    </div>
                                </div>
                            </div>

                            {/* Username */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-300 mb-2">
                                    Username
                                </label>
                                <div className="relative">
                                    <Type className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="text"
                                        name="username"
                                        value={formData.username}
                                        onChange={handleChange}
                                        className="input-field pl-12"
                                        placeholder="your_username"
                                        pattern="[a-zA-Z0-9_]{3,30}"
                                        title="3-30 characters, letters, numbers, and underscores only"
                                        disabled
                                    />
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                    Username cannot be changed
                                </p>
                            </div>

                            {/* Display Name */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-300 mb-2">
                                    Display Name *
                                </label>
                                <div className="relative">
                                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="text"
                                        name="displayName"
                                        value={formData.displayName}
                                        onChange={handleChange}
                                        className="input-field pl-12"
                                        placeholder="Your Display Name"
                                        maxLength="100"
                                        required
                                    />
                                </div>
                            </div>

                            {/* Artist Name */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-300 mb-2">
                                    Artist Name
                                </label>
                                <div className="relative">
                                    <Star className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="text"
                                        name="artistName"
                                        value={formData.artistName}
                                        onChange={handleChange}
                                        className="input-field pl-12"
                                        placeholder="Your artist or stage name"
                                        maxLength="100"
                                    />
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                    Shown on your profile below your display name
                                </p>
                            </div>

                            {/* Bio */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-300 mb-2">
                                    Bio
                                </label>
                                <textarea
                                    name="bio"
                                    value={formData.bio}
                                    onChange={handleChange}
                                    className="input-field min-h-[100px] resize-vertical"
                                    placeholder="Tell us about yourself..."
                                    maxLength="500"
                                    rows="4"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    {formData.bio?.length || 0}/500 characters
                                </p>
                            </div>

                            {/* Location */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-300 mb-2">
                                    Location
                                </label>
                                <div className="relative">
                                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="text"
                                        name="location"
                                        value={formData.location}
                                        onChange={handleChange}
                                        className="input-field pl-12"
                                        placeholder="City, Country"
                                        maxLength="100"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Artist Settings */}
                    <div className="glass-card p-8">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                                <Star className="w-6 h-6 text-pink-400" />
                                Artist Status
                            </h2>
                            <button
                                type="button"
                                onClick={handleArtistToggle}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors duration-300 ${
                                    formData.isArtist 
                                        ? 'bg-pink-500/20 text-pink-300 hover:bg-pink-500/30'
                                        : 'bg-gray-700/50 text-gray-400 hover:bg-gray-700'
                                }`}
                            >
                                {formData.isArtist ? (
                                    <ToggleRight className="w-6 h-6" />
                                ) : (
                                    <ToggleLeft className="w-6 h-6" />
                                )}
                                {formData.isArtist ? 'Artist' : 'Fan'}
                            </button>
                        </div>

                        {formData.isArtist && (
                            <div className="space-y-6 pt-6 border-t border-white/10">
                                {/* Art Types */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                                        Type of Artist
                                        <span className="text-xs text-gray-500 font-normal ml-2">(select all that apply)</span>
                                    </label>
                                    <div className="flex flex-wrap gap-2">
                                        {ARTIST_TYPES.map(type => {
                                            const selected = formData.artistTypes.includes(type.key);
                                            return (
                                                <button
                                                    key={type.key}
                                                    type="button"
                                                    onClick={() => handleArtistTypeToggle(type.key)}
                                                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                                                        selected
                                                            ? 'bg-purple-500/20 text-purple-300 border-purple-500/40'
                                                            : 'bg-gray-800/50 text-gray-300 border-gray-600 hover:border-gray-500'
                                                    }`}
                                                >
                                                    {selected && <Check className="w-4 h-4" />}
                                                    {type.label}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {formData.artistTypes.includes('other') && (
                                        <div className="mt-3">
                                            <input
                                                type="text"
                                                value={formData.customArtistType}
                                                onChange={(e) => setFormData(prev => ({ ...prev, customArtistType: e.target.value }))}
                                                className="input-field"
                                                placeholder="Name your type of art (e.g. Puppetry, Tattoo Art, Culinary Art...)"
                                                maxLength="100"
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Genres/styles per selected type */}
                                {formData.artistTypes.map(typeKey => {
                                    const type = ARTIST_TYPES.find(t => t.key === typeKey);
                                    if (!type || type.genres.length === 0) return null;
                                    const selectedGenres = formData.genres[typeKey] || [];
                                    const dropdownOpen = openGenreDropdown === typeKey;

                                    return (
                                        <div key={typeKey}>
                                            <label className="block text-sm font-semibold text-gray-300 mb-2">
                                                {type.label} Genres/Styles
                                            </label>
                                            <div className="relative">
                                                <button
                                                    type="button"
                                                    onClick={() => setOpenGenreDropdown(dropdownOpen ? null : typeKey)}
                                                    className="input-field flex items-center justify-between"
                                                >
                                                    <span className="text-gray-400">
                                                        {selectedGenres.length > 0
                                                            ? `${selectedGenres.length} selected`
                                                            : `Select ${type.label.toLowerCase()} genres/styles...`
                                                        }
                                                    </span>
                                                    <Plus className={`w-5 h-5 transform transition-transform ${dropdownOpen ? 'rotate-45' : ''}`} />
                                                </button>

                                                {dropdownOpen && (
                                                    <div className="absolute z-10 w-full mt-2 bg-gray-800 border border-gray-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                                        {type.genres.map(genre => (
                                                            <button
                                                                key={genre}
                                                                type="button"
                                                                onClick={() => handleGenreToggle(typeKey, genre)}
                                                                className={`w-full px-4 py-3 text-left hover:bg-gray-700 transition-colors ${
                                                                    selectedGenres.includes(genre)
                                                                        ? 'bg-purple-500/20 text-purple-300'
                                                                        : 'text-gray-300'
                                                                }`}
                                                            >
                                                                {genre}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>

                                            {selectedGenres.length > 0 && (
                                                <div className="mt-3 flex flex-wrap gap-2">
                                                    {selectedGenres.map(genre => (
                                                        <span
                                                            key={genre}
                                                            className="inline-flex items-center gap-2 px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm"
                                                        >
                                                            {genre}
                                                            <button
                                                                type="button"
                                                                onClick={() => handleGenreToggle(typeKey, genre)}
                                                                className="hover:text-red-300"
                                                            >
                                                                <X className="w-3 h-3" />
                                                            </button>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {!formData.isArtist && (
                            <p className="text-gray-400 text-center py-8 border-t border-white/10">
                                Enable artist status to select the type of art you make and your genres/styles
                            </p>
                        )}
                    </div>

                    {/* Submit Button */}
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                        <button
                            type="button"
                            onClick={handleDeleteProfile}
                            disabled={loading || deleting}
                            className="w-full sm:w-auto flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm sm:text-base rounded-lg transition-colors disabled:opacity-50"
                        >
                            {deleting ? (
                                <>
                                    <Loader className="w-4 h-4 animate-spin" />
                                    Deleting...
                                </>
                            ) : (
                                <>
                                    <Trash2 className="w-4 h-4" />
                                    Delete Profile
                                </>
                            )}
                        </button>

                        <div className="flex gap-3 w-full sm:w-auto">
                            <button
                                type="button"
                                onClick={() => navigate(`/profile/${user?.profile?.username}`)}
                                className="btn-secondary flex-1 sm:flex-none !px-4 !py-2 text-sm sm:text-base"
                                disabled={loading || deleting}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading || deleting}
                                className="btn-primary flex-1 sm:flex-none flex items-center justify-center gap-2 !px-4 !py-2 text-sm sm:text-base disabled:opacity-50"
                            >
                                {loading ? (
                                    <>
                                        <Loader className="w-4 h-4 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4" />
                                        Save Changes
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </form>
            </div>

            {/* Toast Notification */}
            <Toast
                message={toast.message}
                type={toast.type}
                isVisible={toast.show}
                onClose={() => setToast({ ...toast, show: false })}
            />

            <AlbumFormModal
                isOpen={showAlbumModal}
                editingAlbum={editingAlbum}
                onClose={() => setShowAlbumModal(false)}
                onSuccess={() => {
                    loadAlbums();
                    setToast({ show: true, message: editingAlbum ? 'Album updated' : 'Album created', type: 'success' });
                }}
            />
        </div>
    );
};

export default EditProfile;